"use client";

import { useEffect } from "react";

// Detect errors caused by deployment skew (old cached HTML referencing
// JS/CSS chunks that no longer exist after a new build).
// These are safe to auto-reload because a fresh page load will fetch
// the latest assets.
function isDeploymentSkewError(error: Error): boolean {
  const msg = error.message || "";
  const name = error.name || "";
  return (
    name === "ChunkLoadError" ||
    msg.includes("Loading chunk") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Load failed") ||
    msg.includes("CSS_CHUNK_LOAD_FAILED") ||
    msg.includes("insertBefore") ||
    msg.includes("removeChild") ||
    msg.includes("Minified React error")
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Global Error:", error);

    // Auto-reload once for deployment skew errors.
    // Use sessionStorage to prevent infinite reload loops.
    if (isDeploymentSkewError(error)) {
      const key = "global-error-reload";
      const lastReload = sessionStorage.getItem(key);
      const now = Date.now();

      // Allow one auto-reload per 60 seconds
      if (!lastReload || now - parseInt(lastReload, 10) > 60_000) {
        sessionStorage.setItem(key, now.toString());
        window.location.reload();
        return;
      }
    }
  }, [error]);

  return (
    <html>
      <body>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <h2 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#1f2937", marginBottom: "1rem" }}>
            Whoops, something isn&apos;t right
          </h2>
          <p style={{ color: "#4b5563", marginBottom: "1.5rem" }}>
            This usually happens after a site update. A quick reload should fix it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1.5rem",
              backgroundColor: "#2563eb",
              color: "#fff",
              borderRadius: "0.5rem",
              border: "none",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            Reload Page
          </button>
        </div>
      </body>
    </html>
  );
}
